import {
    formatBackendDatePtBr,
    formatIsoDate,
    parseBackendDate
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { parseNumberPtBrOrThrow } from '../../utils/numberUtils';
import { isBlank, normalizeText } from '../../utils/stringFormat';

export const fetchIndicacoesMes = async ({ dataInicio, dataFim }) => {
    const response = await fetchFlask('getIndicacoesMes', JSON.stringify({
        data_inicio: dataInicio,
        data_fim: dataFim
    }));
    return response.indicacoes;
};

export const isConcludedIndication = (indicacao) => (
    Boolean(indicacao.data_conclusao) ||
    normalizeText(indicacao.status).includes('conclu')
);

export const isContractPhaseIndication = (indicacao) => (
    String(indicacao.status) === '1390251610' ||
    String(indicacao.status) === '4'
);

export const parseIndicationValue = (value) => (
    isBlank(value)
        ? 0
        : parseNumberPtBrOrThrow(value, `Valor inválido recebido para indicação: ${value}`)
);

export const parseBaseDays = (value) => (
    isBlank(value)
        ? null
        : parseNumberPtBrOrThrow(value, `Dias na base inválido recebido para indicação: ${value}`)
);

export const buildIndicationSummary = (indicacoes) => {
    const concluded = indicacoes.filter(isConcludedIndication);
    const contractPhase = indicacoes.filter(isContractPhaseIndication);
    const indicatorClientIds = new Set(
        indicacoes
            .map((indicacao) => indicacao.cliente_id)
            .filter((clienteId) => clienteId !== null && clienteId !== undefined && clienteId !== '')
    );
    const totalValue = indicacoes.reduce((total, indicacao) => total + parseIndicationValue(indicacao.valor), 0);
    const conqueredValue = concluded.reduce((total, indicacao) => total + parseIndicationValue(indicacao.valor), 0);

    return {
        totalIndications: indicacoes.length,
        totalValue,
        conqueredValue,
        concludedIndications: concluded.length,
        contractPhaseIndications: contractPhase.length,
        indicatorClients: indicatorClientIds.size,
        averageTicket: indicacoes.length ? totalValue / indicacoes.length : 0,
        conversionRate: indicacoes.length ? concluded.length / indicacoes.length : 0
    };
};

export const buildDailyIndications = (indicacoes, dataInicio, dataFim) => {
    const daysMap = new Map();
    const startDate = new Date(dataInicio.getFullYear(), dataInicio.getMonth(), 1);
    const endDate = new Date(dataFim.getFullYear(), dataFim.getMonth() + 1, 0);

    for (let offset = 0; offset <= endDate.getTime() - startDate.getTime(); offset += 86400000) {
        const currentDate = new Date(startDate.getTime() + offset);
        daysMap.set(formatIsoDate(currentDate), { indicated: 0, concluded: 0 });
    }

    const addToDay = (value, seriesKey) => {
        const date = parseBackendDate(value);
        if (!date) return;

        const dayKey = formatIsoDate(date);
        const dayCounts = daysMap.get(dayKey);
        if (!dayCounts) return;

        dayCounts[seriesKey] += 1;
    };

    indicacoes.forEach((indicacao) => {
        addToDay(indicacao.data_indicacao, 'indicated');

        if (isConcludedIndication(indicacao)) {
            addToDay(indicacao.data_conclusao, 'concluded');
        }
    });

    return Array.from(daysMap.entries())
        .sort(([leftDay], [rightDay]) => leftDay.localeCompare(rightDay))
        .map(([day, dayCounts]) => ({
            day,
            label: formatBackendDatePtBr(`${day}T00:00:00`),
            indicated: dayCounts.indicated,
            concluded: dayCounts.concluded
        }));
};

export const buildIndicationRows = (indicacoes) => {
    const indicationMap = new Map();

    indicacoes.forEach((indicacao) => {
        const indicatorClient = indicacao.cliente || 'Sem cliente indicador';
        const currentIndicator = indicationMap.get(indicatorClient) || {
            id: indicatorClient,
            clienteIndicador: indicatorClient,
            ticketIndicado: 0,
            ticketConquistado: 0,
            totalIndicacoes: 0,
            totalIndicacoesConcluidas: 0,
            diasBase: null
        };
        const indicationValue = parseIndicationValue(indicacao.valor);
        const baseDays = parseBaseDays(indicacao.dias_base);

        currentIndicator.ticketIndicado += indicationValue;
        currentIndicator.totalIndicacoes += 1;

        if (baseDays !== null) {
            currentIndicator.diasBase = currentIndicator.diasBase === null
                ? baseDays
                : Math.max(currentIndicator.diasBase, baseDays);
        }

        if (isConcludedIndication(indicacao)) {
            currentIndicator.ticketConquistado += indicationValue;
            currentIndicator.totalIndicacoesConcluidas += 1;
        }

        indicationMap.set(indicatorClient, currentIndicator);
    });

    return Array.from(indicationMap.values());
};
